import { useEffect } from 'react';
import { useTranslation } from '@frontend/i18n';
import { useLocationGroupStore, useAppScopeSetters } from '@frontend/scope';
import { useAlert } from '@frontend/design-system';
import { useWorkspaceStore } from '../store';
import { useValidateSelectedWorkspace } from './use-validate-selected-workspace';
import { useWorkspaceFeature } from './use-workspace-feature';

/**
 * Auto-heals invalid workspace state on app mount.
 * - Validates workspace exists
 * - Clears workspace + forces location picker if invalid
 * - Shows warning alert to user
 *
 * **Performance:** Uses granular selectors instead of `useAppScopeSelection` (10 fields).
 */
export function useWorkspaceAutoHeal() {
  const { t } = useTranslation('base');
  const alerts = useAlert();
  const selectedOrgId = useLocationGroupStore((state) => state.selectedOrgId);
  const { isInWorkspaceScope } = useWorkspaceFeature();
  const { setSelectedScope, setSelectedLocationIds, setSelectedGroupId } = useAppScopeSetters();
  const setSelectedWorkspaceId = useWorkspaceStore((state) => state.setSelectedWorkspaceId);
  const validation = useValidateSelectedWorkspace(selectedOrgId);

  useEffect(() => {
    if (!validation.shouldAutoHeal || !isInWorkspaceScope) return;

    // Debounce the heal so transient validation states (e.g. data-loading race
    // on mount) don't trigger a premature scope reset. The timer is cancelled
    // on cleanup, so rapid dep changes or unmount-before-fire are both safe.
    const timer = setTimeout(() => {
      // Clear workspace state
      setSelectedWorkspaceId(null);
      setSelectedScope(undefined);
      // Force LOCATION_SELECTION screen by clearing location selection
      setSelectedLocationIds([], { skipValidation: true });
      setSelectedGroupId('', { skipValidation: true });
      // Show warning alert
      alerts.warning(t('⚠️ The selected workspace is no longer available. Please select your locations.'));
    }, 300);

    return () => clearTimeout(timer);
  }, [
    validation.shouldAutoHeal,
    isInWorkspaceScope,
    setSelectedWorkspaceId,
    setSelectedScope,
    setSelectedLocationIds,
    setSelectedGroupId,
    alerts,
    t,
  ]);
}
